import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { filter, escapeRegExp, get, includes, isArray, isEmpty } from 'lodash';
import { getSquigglyTemplates } from 'ui-utils';
import { squigglyTemplates as utilsSquigglies } from 'ui-utils/dist/getters/constants/squigglyTemplates';
import { RolloutEnum } from '@lib/resources/types';
import usePermissions from 'hooks/usePermissions';
import { useCanZoomSquiggly } from 'deprecated/fragments/hooks/useCanZoomSquiggly';
import { QueryListSquiggliesQuery } from '../__generated__/QueryListSquiggliesQuery.graphql';

export const useFilterSquigglies = (
  squigglies: keyof typeof utilsSquigglies | string[],
  viewer: QueryListSquiggliesQuery['response']['viewer'],
  query?: string,
  patientFormTemplates?: { id: string; name: string }[] | null,
  lumabotFlowTemplates?: { id: string; name: string }[] | null,
) => {
  const canUseZoomSquiggly = useCanZoomSquiggly();
  const { formatMessage } = useIntl();
  const { hasAccessToProduct, permissions } = usePermissions();
  const squigglyRegexp = useMemo(() => query && new RegExp(escapeRegExp(query), 'gi'), [query]);
  const provisioning = get(viewer, 'salesforceData.provisioning', []);

  const hasAccessToChecklist = hasAccessToProduct(RolloutEnum.AppointmentChecklist) || permissions?.isDemoAccount;

  const squigglyTemplates = useMemo(() => {
    const ids =
      isArray(squigglies) && (!isEmpty(patientFormTemplates) || !isEmpty(lumabotFlowTemplates))
        ? // Skip form squigglies and use the ones fetched to include proper names
          filter(squigglies, (squiggly) => !/custom-form/.test(squiggly) || !/lumabot-flow/.test(squiggly))
        : squigglies;
    return getSquigglyTemplates(ids, formatMessage);
  }, [squigglies, patientFormTemplates, lumabotFlowTemplates, formatMessage]);

  const squiggliesFiltered = useMemo(
    () =>
      filter(
        squigglyTemplates,
        (squiggly) =>
          // Filter squigglies by query
          (!squigglyRegexp || squigglyRegexp.test(squiggly.id) || squigglyRegexp.test(squiggly.name)) &&
          // Filter squigglies by provisioning
          (!squiggly.provisioning || includes(provisioning, squiggly.provisioning)) &&
          (!squiggly.id.includes('{{system.url.adhocTelehealth}}') || canUseZoomSquiggly),
      ),
    [canUseZoomSquiggly, squigglyTemplates, provisioning, squigglyRegexp],
  );

  const squiggliesList = hasAccessToChecklist
    ? squiggliesFiltered
    : filter(squiggliesFiltered, (squiggly) => squiggly.id !== '{{system.url.appointmentChecklist}}');

  return squiggliesList;
};
